import { GetStaticPaths, GetStaticProps } from 'next';
import { NavigationItemFragment, PageDetailFragment } from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import {
  getNavigation,
  getPartners,
  NavigationProps,
  NavigationType,
} from '@amf/shared/utils/navigation';
import { getContentDescription, getContentImageUrl } from '@amf/shared/utils/content';
import Container from '@amf/shared/components/layout/Container';
import Content from '@amf/shared/components/content/Content';
import { getLanguageLocale } from '@amf/shared/utils/languages';

import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';

interface StaticPageProps extends NavigationProps {
  page: PageDetailFragment;
}

export default function StaticPage({ page, navigation, partners }: StaticPageProps) {
  const imageUrl = getContentImageUrl(page.content);
  const description = getContentDescription(page.content);
  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta title={page.title} image={imageUrl} description={description} />
      <Container>
        <h1>{page.title}</h1>
        <Content content={page.content} />
      </Container>
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  let paths: { params: { path: string[] }; locale: string }[] = [];

  for (const locale of locales ?? []) {
    const navigation = await getNavigation(getLanguageLocale(locale));
    navigation?.forEach(item => {
      if (item.type === NavigationType.link) {
        return;
      }

      if (item.type === NavigationType.page) {
        paths.push({ params: { path: [item.path] }, locale });
        return;
      }

      for (const itemChild of item.children ?? []) {
        if (!itemChild) {
          continue;
        }

        if (itemChild.type === NavigationType.link) {
          continue;
        }

        paths.push({ params: { path: itemChild.path.split('/') }, locale });
      }
    });
  }

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { path } = params as { path: string[] };
  const slug = path.slice(-1).pop();
  if (!slug) {
    return {
      notFound: true,
    };
  }

  let navigation: NavigationItemFragment[];
  let page: PageDetailFragment | null | undefined;
  try {
    const data = await sdk.StaticPagePage({
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale ?? 'cz'),
      path: slug,
    });
    navigation = data.navigation;
    page = data.page;
  } catch {
    return {
      notFound: true,
    };
  }

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      navigation,
      page,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
